import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = safeStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#2d3748',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '0.95rem',
    color: '#718096',
  },
  // Stats dashboard
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '28px',
  },
  statCard: {
    background: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#2563eb',
  },
  statLabel: {
    fontSize: '0.8rem',
    color: '#718096',
    marginTop: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  // Layout
  mainLayout: {
    display: 'grid',
    gridTemplateColumns: '340px 1fr',
    gap: '24px',
    alignItems: 'start',
  },
  // Manufacturer sidebar
  mfrPanel: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  panelHeader: {
    background: '#2563eb',
    color: 'white',
    padding: '16px 20px',
    fontWeight: '600',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
  },
  mfrSearch: {
    padding: '12px 16px',
    borderBottom: '1px solid #e2e8f0',
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  mfrList: {
    maxHeight: '600px',
    overflowY: 'auto',
  },
  mfrItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.15s',
  },
  mfrItemActive: {
    background: '#ebf4ff',
    borderLeft: '3px solid #2563eb',
  },
  mfrName: {
    fontWeight: '500',
    color: '#2d3748',
    fontSize: '0.9rem',
  },
  mfrBadge: {
    background: '#fed7d7',
    color: '#c53030',
    fontSize: '0.75rem',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
  },
  // Right panel
  rightPanel: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  rightPanelHeader: {
    background: '#38b2ac',
    color: 'white',
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rightPanelHeaderTitle: {
    fontWeight: '600',
    fontSize: '1rem',
  },
  rightPanelContent: {
    padding: '20px',
  },
  // Buttons
  btn: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.85rem',
    transition: 'all 0.2s',
  },
  btnPrimary: {
    background: '#2563eb',
    color: 'white',
  },
  btnSuccess: {
    background: '#28a745',
    color: 'white',
  },
  btnDanger: {
    background: '#dc3545',
    color: 'white',
  },
  btnWarning: {
    background: '#f59e0b',
    color: 'white',
  },
  btnOutline: {
    background: 'transparent',
    color: '#2563eb',
    border: '2px solid #2563eb',
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  btnSmall: {
    padding: '4px 10px',
    fontSize: '0.78rem',
  },
  // Duplicate group card
  groupCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    marginBottom: '16px',
    overflow: 'hidden',
  },
  groupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    cursor: 'pointer',
  },
  groupModel: {
    fontWeight: '700',
    fontSize: '1rem',
    color: '#2d3748',
  },
  groupCount: {
    fontSize: '0.8rem',
    color: '#e53e3e',
    fontWeight: '600',
  },
  groupBody: {
    padding: '12px 16px',
  },
  // Amp row inside group
  ampRow: {
    display: 'grid',
    gridTemplateColumns: '60px 1fr auto',
    gap: '12px',
    alignItems: 'center',
    padding: '10px 12px',
    borderRadius: '8px',
    marginBottom: '6px',
    background: '#fafafa',
    border: '1px solid #edf2f7',
  },
  ampId: {
    fontFamily: 'monospace',
    fontSize: '0.82rem',
    color: '#718096',
    fontWeight: '600',
  },
  ampDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  ampModel: {
    fontWeight: '600',
    color: '#2d3748',
    fontSize: '0.9rem',
  },
  ampMeta: {
    fontSize: '0.78rem',
    color: '#a0aec0',
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  ampMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
  },
  ampDataBadges: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  dataBadge: {
    fontSize: '0.7rem',
    padding: '2px 7px',
    borderRadius: '10px',
    fontWeight: '600',
  },
  // Action card (AI suggestion)
  actionCard: {
    border: '2px solid #e2e8f0',
    borderRadius: '10px',
    padding: '14px 16px',
    marginTop: '12px',
    background: '#fffff0',
  },
  actionType: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '6px',
    fontSize: '0.78rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    marginRight: '8px',
  },
  actionReason: {
    fontSize: '0.85rem',
    color: '#4a5568',
    marginTop: '8px',
    lineHeight: '1.5',
  },
  actionDetail: {
    fontSize: '0.82rem',
    color: '#718096',
    marginTop: '6px',
    padding: '8px 12px',
    background: '#f7fafc',
    borderRadius: '6px',
    fontFamily: 'monospace',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    justifyContent: 'flex-end',
  },
  // Status badges for actions
  statusApproved: {
    background: '#c6f6d5',
    color: '#22543d',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: '600',
  },
  statusRejected: {
    background: '#fed7d7',
    color: '#742a2a',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: '600',
  },
  statusPending: {
    background: '#fefcbf',
    color: '#744210',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: '600',
  },
  // Info box
  infoBox: {
    background: '#ebf8ff',
    border: '1px solid #90cdf4',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  infoText: {
    fontSize: '0.88rem',
    color: '#2c5282',
    lineHeight: '1.5',
  },
  // Progress bar
  progressBar: {
    width: '100%',
    height: '8px',
    background: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '16px',
  },
  progressFill: {
    height: '100%',
    background: '#2563eb',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  // Empty state
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#a0aec0',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '12px',
  },
  // Loading
  loadingSpinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
    flexDirection: 'column',
    gap: '12px',
    color: '#718096',
  },
  // Result messages
  successMessage: {
    background: '#c6f6d5',
    border: '1px solid #68d391',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    color: '#22543d',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorMessage: {
    background: '#fed7d7',
    border: '1px solid #fc8181',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    color: '#742a2a',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  // Execute summary
  summaryBox: {
    background: '#f0fff4',
    border: '1px solid #9ae6b4',
    borderRadius: '10px',
    padding: '16px 20px',
    marginTop: '20px',
  },
  summaryTitle: {
    fontWeight: '700',
    fontSize: '1rem',
    color: '#22543d',
    marginBottom: '8px',
  },
  summaryItem: {
    fontSize: '0.88rem',
    color: '#276749',
    padding: '4px 0',
  },
  // Rejection feedback
  rejectCommentBox: {
    marginTop: '12px',
    padding: '12px',
    background: '#fff5f5',
    border: '1px solid #feb2b2',
    borderRadius: '8px',
  },
  rejectTextarea: {
    width: '100%',
    minHeight: '70px',
    padding: '8px 10px',
    fontSize: '0.85rem',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    outline: 'none',
  },
  rejectCommentLabel: {
    fontSize: '0.82rem',
    fontWeight: '600',
    color: '#742a2a',
    marginBottom: '6px',
    display: 'block',
  },
  rejectCommentActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
    justifyContent: 'flex-end',
  },
  rejectHistory: {
    marginTop: '10px',
    padding: '10px 12px',
    background: '#fff5f5',
    borderRadius: '8px',
    border: '1px solid #fed7d7',
  },
  rejectHistoryItem: {
    fontSize: '0.8rem',
    color: '#742a2a',
    padding: '4px 0',
    borderBottom: '1px dashed #fed7d7',
  },
  rejectHistoryLabel: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#9b2c2c',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  reAnalyzeBtn: {
    background: '#6c5ce7',
    color: 'white',
    border: 'none',
    padding: '6px 14px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.82rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  // Tabs
  tabBar: {
    display: 'flex',
    gap: '0',
    marginBottom: '24px',
    borderBottom: '2px solid #e2e8f0',
  },
  tab: {
    padding: '12px 24px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.95rem',
    color: '#718096',
    border: 'none',
    borderBottom: '3px solid transparent',
    background: 'none',
    marginBottom: '-2px',
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#2563eb',
    borderBottomColor: '#2563eb',
  },
  // Manufacturer consolidation
  mfrConsolSearch: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    alignItems: 'flex-end',
  },
  mfrConsolSearchInput: {
    flex: 1,
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
  },
  mfrGroupCard: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    marginBottom: '16px',
    overflow: 'hidden',
  },
  mfrGroupHeader: {
    padding: '14px 20px',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  mfrGroupTitle: {
    fontWeight: '600',
    fontSize: '1rem',
    color: '#2d3748',
  },
  mfrMemberRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 20px',
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.15s',
  },
  mfrMemberName: {
    fontWeight: '500',
    color: '#2d3748',
    fontSize: '0.9rem',
  },
  mfrMemberMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  keeperBadge: {
    background: '#c6f6d5',
    color: '#22543d',
    fontSize: '0.72rem',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '6px',
    textTransform: 'uppercase',
  },
  sourceBadge: {
    background: '#fed7d7',
    color: '#742a2a',
    fontSize: '0.72rem',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '6px',
    textTransform: 'uppercase',
  },
  ampCountBadge: {
    background: '#e2e8f0',
    color: '#4a5568',
    fontSize: '0.78rem',
    fontWeight: '600',
    padding: '2px 10px',
    borderRadius: '10px',
  },
  consolActions: {
    padding: '12px 20px',
    background: '#f7fafc',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  excludedBadge: {
    background: '#e2e8f0',
    color: '#718096',
    fontSize: '0.72rem',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '6px',
    textTransform: 'uppercase',
  },
  mfrMemberExcluded: {
    opacity: 0.5,
    background: '#f7fafc',
    textDecoration: 'line-through',
  },
  renameInput: {
    padding: '4px 8px',
    border: '1px solid #cbd5e0',
    borderRadius: '4px',
    fontSize: '0.88rem',
    fontWeight: '500',
    color: '#2d3748',
    width: '280px',
  },
  exampleAmpsPanel: {
    margin: '0 20px 8px 36px',
    padding: '8px 12px',
    background: '#f7fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '0.82rem',
  },
  exampleAmpRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 0',
    borderBottom: '1px solid #edf2f7',
    color: '#4a5568',
  },
  exampleAmpThumb: {
    width: '48px',
    height: '48px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #e2e8f0',
    background: '#fff',
    flexShrink: 0,
  },
  exampleAmpNoImage: {
    width: '48px',
    height: '48px',
    borderRadius: '4px',
    border: '1px dashed #cbd5e0',
    background: '#edf2f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.2rem',
    color: '#a0aec0',
    flexShrink: 0,
  },
  exampleAmpModel: {
    fontWeight: '600',
    color: '#2d3748',
  },
  exampleAmpYear: {
    fontSize: '0.76rem',
    color: '#a0aec0',
  },
};

// Action type colors
const actionTypeColors = {
  merge: { bg: '#bee3f8', color: '#2b6cb0' },
  rename: { bg: '#fefcbf', color: '#975a16' },
  delete: { bg: '#fed7d7', color: '#c53030' },
  skip: { bg: '#e2e8f0', color: '#4a5568' },
};

// Badge colors for data counts
const badgeColors = {
  children: { bg: '#bee3f8', color: '#2b6cb0' },
  artifacts: { bg: '#c6f6d5', color: '#276749' },
  components: { bg: '#fefcbf', color: '#975a16' },
  discussions: { bg: '#e9d8fd', color: '#553c9a' },
};

// Timer component that ticks every second during AI analysis
const AnalysisTimer = ({ groupCount, startTime }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startTime) return;
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

  return (
    <div>
      <style>{`
        @keyframes indeterminate-progress {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(250%); }
        }
      `}</style>
      <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '6px' }}>
        Gemini is analyzing {groupCount} duplicate groups...
        <strong style={{ marginLeft: '8px', color: '#2d3748' }}>{timeStr}</strong>
      </div>
      <div style={{ ...styles.progressBar, overflow: 'hidden' }}>
        <div style={{
          ...styles.progressFill,
          width: '40%',
          animation: 'indeterminate-progress 1.4s ease-in-out infinite',
        }} />
      </div>
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────
const DuplicateCleanupScreen = () => {
  const { isAdmin } = useAuth();

  // Ref for cancelling auto-process (can't read state synchronously in async loop)
  const autoProcessCancelledRef = React.useRef(false);

  // State
  const [stats, setStats] = useState(null);
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedMfr, setSelectedMfr] = useState(null);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [mfrSearch, setMfrSearch] = useState('');
  const [aiActions, setAiActions] = useState({}); // { model: { action, status: 'pending'|'approved'|'rejected', rejectComment?, rejectHistory? } }
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStartTime, setAnalysisStartTime] = useState(null); // for elapsed timer
  const [executing, setExecuting] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }
  const [executionResults, setExecutionResults] = useState(null);
  const [rejectingModel, setRejectingModel] = useState(null); // model currently showing rejection comment box
  const [rejectComment, setRejectComment] = useState(''); // current rejection comment text
  const [reAnalyzing, setReAnalyzing] = useState(null); // model currently being re-analyzed

  // Tab state
  const [activeTab, setActiveTab] = useState('amps'); // 'amps' | 'manufacturers'

  // Manufacturer consolidation state
  const [mfrConsolSearch, setMfrConsolSearch] = useState('');
  const [mfrConsolGroups, setMfrConsolGroups] = useState([]); // auto-detected groups
  const [mfrConsolSearchResults, setMfrConsolSearchResults] = useState([]); // search results
  const [mfrConsolMode, setMfrConsolMode] = useState('auto'); // 'auto' | 'search'
  const [mfrConsolKeepers, setMfrConsolKeepers] = useState({}); // { group_key: manufacturer_id }
  const [mfrConsolExpanded, setMfrConsolExpanded] = useState({});
  const [mfrConsolExcluded, setMfrConsolExcluded] = useState({}); // { group_key: Set of excluded manufacturer_ids }
  const [mfrConsolLoading, setMfrConsolLoading] = useState(false);
  const [mfrConsolExecuting, setMfrConsolExecuting] = useState(null); // group_key being executed
  const [, setMfrConsolResults] = useState(null);
  const [mfrConsolMessage, setMfrConsolMessage] = useState(null);
  const [mfrRenaming, setMfrRenaming] = useState(null); // { manufacturer_id, name } currently being renamed
  const [mfrRenameSaving, setMfrRenameSaving] = useState(false);
  const [mfrExampleAmps, setMfrExampleAmps] = useState({}); // { manufacturer_id: { loading, amps[] } }
  const [hoverPreview, setHoverPreview] = useState(null); // { src, x, y }
  const hoverTimerRef = React.useRef(null);

  const handleThumbEnter = (e, artifactId) => {
    const rect = e.target.getBoundingClientRect();
    hoverTimerRef.current = setTimeout(() => {
      setHoverPreview({
        src: `${API_URL}/amplifier_artifacts/${artifactId}`,
        x: rect.right + 12,
        y: rect.top,
      });
    }, 300);
  };

  const handleThumbLeave = () => {
    clearTimeout(hoverTimerRef.current);
    setHoverPreview(null);
  };

  // Auto-process state
  const [autoProcessing, setAutoProcessing] = useState(false);
  const [autoProcessProgress, setAutoProcessProgress] = useState(null); // { current, total, mfrName, action, results[] }
  const [, setAutoProcessCancelled] = useState(false);

  // Load stats on mount
  useEffect(() => {
    loadStats();
    loadManufacturers();
  }, []);

  const loadStats = async () => {
    try {
      const res = await api.get('/amplifiers-duplicate-stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadManufacturers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/amplifiers-duplicate-manufacturers');
      setManufacturers(res.data);
    } catch (err) {
      console.error('Failed to load manufacturers:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectManufacturer = async (mfr) => {
    setSelectedMfr(mfr);
    setDuplicateGroups([]);
    setAiActions({});
    setExpandedGroups({});
    setMessage(null);
    setExecutionResults(null);
    setLoadingGroups(true);
    try {
      const res = await api.get(`/amplifiers-duplicates/${mfr.manufacturer_id}`);
      const groups = res.data.duplicate_groups || [];
      setDuplicateGroups(groups);
      // Expand first group by default
      if (groups.length > 0) {
        setExpandedGroups({ [groups[0].model]: true });
      }
    } catch (err) {
      console.error('Failed to load duplicates:', err);
      setMessage({ type: 'error', text: 'Failed to load duplicate groups' });
    } finally {
      setLoadingGroups(false);
    }
  };

  const toggleGroup = (model) => {
    setExpandedGroups(prev => ({ ...prev, [model]: !prev[model] }));
  };

  // ─── AI Analysis ────────────────────────────────────────────────────────
  const analyzeWithAI = async () => {
    if (!selectedMfr || duplicateGroups.length === 0) return;
    setAnalyzing(true);
    setAnalysisStartTime(Date.now());
    setMessage(null);
    try {
      const res = await api.post('/analyze-duplicates', {
        manufacturer_name: selectedMfr.manufacturer_name,
        duplicate_groups: duplicateGroups.map(g => ({
          model: g.model,
          amps: g.amps,
        })),
      });

      const actionsMap = {};
      for (const action of res.data.actions) {
        actionsMap[action.model] = { action, status: 'pending' };
      }
      setAiActions(actionsMap);
      // Expand all groups to see suggestions
      const expanded = {};
      duplicateGroups.forEach(g => { expanded[g.model] = true; });
      setExpandedGroups(expanded);
      setMessage({ type: 'success', text: `AI analyzed ${res.data.actions.length} duplicate groups. Review suggestions below.` });
    } catch (err) {
      console.error('AI analysis failed:', err);
      setMessage({ type: 'error', text: `AI analysis failed: ${err.response?.data?.message || err.message}` });
    } finally {
      setAnalyzing(false);
      setAnalysisStartTime(null);
    }
  };

  const setActionStatus = (model, status) => {
    setAiActions(prev => ({
      ...prev,
      [model]: { ...prev[model], status },
    }));
  };

  const approveAll = () => {
    const updated = { ...aiActions };
    for (const model of Object.keys(updated)) {
      if (updated[model].action.type !== 'skip') {
        updated[model] = { ...updated[model], status: 'approved' };
      }
    }
    setAiActions(updated);
  };

  const approveAllAndExecute = async () => {
    const actionCount = Object.values(aiActions).filter(v => v.action.type !== 'skip').length;
    if (actionCount === 0) return;
    if (!window.confirm(
      `Approve and execute ALL ${actionCount} AI suggestions without review?\n\n` +
      `This will immediately merge, rename, and delete as suggested. This cannot be undone.`
    )) return;
    // Approve all first
    const updated = { ...aiActions };
    for (const model of Object.keys(updated)) {
      if (updated[model].action.type !== 'skip') {
        updated[model] = { ...updated[model], status: 'approved' };
      }
    }
    setAiActions(updated);
    // Small delay to let state update, then execute
    setTimeout(() => executeApproved(updated), 50);
  };

  const autoProcessAll = async () => {
    const mfrList = [...manufacturers]; // snapshot current list
    if (mfrList.length === 0) return;
    autoProcessCancelledRef.current = false;
    if (!window.confirm(
      `Auto-process ALL ${mfrList.length} remaining manufacturers with AI?\n\n` +
      `For each manufacturer, this will:\n` +
      `  1. Load duplicate groups\n` +
      `  2. Run AI analysis\n` +
      `  3. Execute all non-skip suggestions\n\n` +
      `You can cancel at any time. Continue?`
    )) return;

    setAutoProcessing(true);
    setAutoProcessCancelled(false);
    const results = [];
    setAutoProcessProgress({ current: 0, total: mfrList.length, mfrName: '', action: 'Starting...', results });

    for (let i = 0; i < mfrList.length; i++) {
      // Check if cancelled
      // We use a ref-like pattern: read from state via a flag
      if (autoProcessCancelledRef.current) {
        results.push({ mfr: mfrList[i].manufacturer_name, status: 'cancelled' });
        break;
      }

      const mfr = mfrList[i];
      setAutoProcessProgress(prev => ({ ...prev, current: i + 1, mfrName: mfr.manufacturer_name, action: 'Loading duplicates...' }));
      setSelectedMfr(mfr);

      try {
        // Step 1: Load duplicate groups
        const dupRes = await api.get(`/amplifiers-duplicates/${mfr.manufacturer_id}`);
        const groups = dupRes.data.duplicate_groups || [];
        setDuplicateGroups(groups);

        if (groups.length === 0) {
          results.push({ mfr: mfr.manufacturer_name, status: 'skip', detail: 'No duplicates' });
          setAutoProcessProgress(prev => ({ ...prev, results: [...results] }));
          continue;
        }

        // Step 2: AI analysis
        setAutoProcessProgress(prev => ({ ...prev, action: `Analyzing ${groups.length} groups with AI...` }));
        const aiRes = await api.post('/analyze-duplicates', {
          manufacturer_name: mfr.manufacturer_name,
          duplicate_groups: groups.map(g => ({ model: g.model, amps: g.amps })),
        });

        const actions = aiRes.data.actions || [];
        const actionsMap = {};
        for (const action of actions) {
          if (action.type !== 'skip') {
            actionsMap[action.model] = { action, status: 'approved' };
          }
        }
        setAiActions(actionsMap);

        const approvedActions = Object.values(actionsMap).map(v => v.action);
        if (approvedActions.length === 0) {
          results.push({ mfr: mfr.manufacturer_name, status: 'skip', detail: `AI said skip all ${groups.length} groups` });
          setAutoProcessProgress(prev => ({ ...prev, results: [...results] }));
          continue;
        }

        // Step 3: Execute
        setAutoProcessProgress(prev => ({ ...prev, action: `Executing ${approvedActions.length} actions...` }));
        const execRes = await api.post('/amplifiers-execute-cleanup', { actions: approvedActions });
        const successCount = execRes.data.results.filter(r => r.status === 'success').length;
        const failCount = execRes.data.results.filter(r => r.status === 'error').length;
        const skipCount = actions.filter(a => a.type === 'skip').length;

        results.push({
          mfr: mfr.manufacturer_name,
          status: failCount > 0 ? 'partial' : 'success',
          detail: `${successCount} executed, ${failCount} failed, ${skipCount} skipped`,
        });
      } catch (err) {
        console.error(`Auto-process failed for ${mfr.manufacturer_name}:`, err);
        results.push({ mfr: mfr.manufacturer_name, status: 'error', detail: err.message });
      }
      setAutoProcessProgress(prev => ({ ...prev, results: [...results] }));
    }

    // Refresh everything
    await loadStats();
    await loadManufacturers();
    setAutoProcessing(false);

    const successTotal = results.filter(r => r.status === 'success').length;
    const errorTotal = results.filter(r => r.status === 'error' || r.status === 'partial').length;
    setMessage({
      type: errorTotal > 0 ? 'error' : 'success',
      text: `Auto-processed ${results.length} manufacturers: ${successTotal} clean, ${errorTotal} with issues, ${results.filter(r => r.status === 'skip').length} skipped.`,
    });
  };

  // ─── Rejection with Comment ─────────────────────────────────────────────
  const submitRejection = useCallback((model) => {
    if (!rejectComment.trim()) {
      setMessage({ type: 'error', text: 'Please provide a reason for rejecting this suggestion.' });
      return;
    }
    setAiActions(prev => {
      const entry = prev[model];
      const history = entry.rejectHistory || [];
      return {
        ...prev,
        [model]: {
          ...entry,
          status: 'rejected',
          rejectComment: rejectComment.trim(),
          rejectHistory: [...history, { suggestion: entry.action, reason: rejectComment.trim() }],
        },
      };
    });
    setRejectingModel(null);
    setRejectComment('');
  }, [rejectComment]);

  const cancelRejection = () => {
    setRejectingModel(null);
    setRejectComment('');
  };

  // ─── Re-analyze Single Group with Feedback ──────────────────────────────
  const reAnalyzeGroup = useCallback(async (model) => {
    const entry = aiActions[model];
    if (!entry || !selectedMfr) return;

    const group = duplicateGroups.find(g => g.model === model);
    if (!group) return;

    setReAnalyzing(model);
    setMessage(null);
    try {
      const res = await api.post('/reanalyze-duplicate', {
        manufacturer_name: selectedMfr.manufacturer_name,
        group: {
          model: group.model,
          amps: group.amps,
        },
        previous_suggestion: entry.action,
        rejection_reason: entry.rejectComment,
        rejection_history: entry.rejectHistory || [],
      });

      const newAction = res.data.action;
      setAiActions(prev => ({
        ...prev,
        [model]: {
          ...prev[model],
          action: newAction,
          status: 'pending',
          rejectComment: null, // clear current comment, history is preserved
        },
      }));
      setMessage({ type: 'success', text: `AI provided a revised suggestion for "${model}". Review below.` });
    } catch (err) {
      console.error('Re-analysis failed:', err);
      setMessage({ type: 'error', text: `Re-analysis failed: ${err.response?.data?.message || err.message}` });
    } finally {
      setReAnalyzing(null);
    }
  }, [aiActions, selectedMfr, duplicateGroups]);

  // ─── Execute Approved Actions ───────────────────────────────────────────
  const executeApproved = async (actionsOverride) => {
    const actionsToUse = actionsOverride || aiActions;
    const approved = Object.entries(actionsToUse)
      .filter(([, v]) => v.status === 'approved')
      .map(([, v]) => v.action);

    if (approved.length === 0) {
      setMessage({ type: 'error', text: 'No approved actions to execute.' });
      return;
    }

    const confirmMsg = `Execute ${approved.length} approved action(s)?\n\n` +
      approved.map(a => `• ${a.type.toUpperCase()}: ${a.model}`).join('\n') +
      '\n\nThis will modify the database.';
    
    if (!window.confirm(confirmMsg)) return;

    setExecuting(true);
    setMessage(null);
    try {
      const res = await api.post('/amplifiers-execute-cleanup', { actions: approved });
      setExecutionResults(res.data.results);
      
      const successCount = res.data.results.filter(r => r.status === 'success').length;
      const failCount = res.data.results.filter(r => r.status === 'error').length;
      
      if (failCount === 0) {
        setMessage({ type: 'success', text: `All ${successCount} actions executed successfully!` });
      } else {
        setMessage({ type: 'error', text: `${successCount} succeeded, ${failCount} failed. See details below.` });
      }

      // Refresh data
      await loadStats();
      await loadManufacturers();
      if (selectedMfr) {
        try {
          const res2 = await api.get(`/amplifiers-duplicates/${selectedMfr.manufacturer_id}`);
          setDuplicateGroups(res2.data.duplicate_groups || []);
          // Clear actions for resolved groups
          const remainingModels = new Set((res2.data.duplicate_groups || []).map(g => g.model));
          const remainingActions = {};
          for (const [model, action] of Object.entries(aiActions)) {
            if (remainingModels.has(model)) {
              remainingActions[model] = action;
            }
          }
          setAiActions(remainingActions);
        } catch {
          setDuplicateGroups([]);
          setAiActions({});
        }
      }
    } catch (err) {
      console.error('Execution failed:', err);
      setMessage({ type: 'error', text: `Execution failed: ${err.response?.data?.error || err.message}` });
    } finally {
      setExecuting(false);
    }
  };

  // ─── Manufacturer Consolidation ──────────────────────────────────────────
  const loadMfrDuplicates = async () => {
    setMfrConsolLoading(true);
    setMfrConsolMessage(null);
    setMfrConsolResults(null);
    try {
      const res = await api.get('/manufacturers-find-duplicates');
      setMfrConsolGroups(res.data.groups || []);
      setMfrConsolMode('auto');
      setMfrConsolSearchResults([]);
      // Auto-select keepers (highest amp_count in each group)
      const keepers = {};
      for (const group of (res.data.groups || [])) {
        if (group.members.length > 0) {
          keepers[group.group_key] = group.members[0].manufacturer_id; // already sorted by amp_count DESC
        }
      }
      setMfrConsolKeepers(keepers);
      // Auto-expand groups with > 2 members or well-known names
      const expanded = {};
      for (const group of (res.data.groups || [])) {
        if (group.total_members > 2) expanded[group.group_key] = true;
      }
      setMfrConsolExpanded(expanded);
    } catch (err) {
      console.error('Failed to load manufacturer duplicates:', err);
      setMfrConsolMessage({ type: 'error', text: 'Failed to detect duplicate manufacturers' });
    } finally {
      setMfrConsolLoading(false);
    }
  };

  const searchMfrDuplicates = async () => {
    if (!mfrConsolSearch.trim()) {
      loadMfrDuplicates();
      return;
    }
    setMfrConsolLoading(true);
    setMfrConsolMessage(null);
    try {
      const res = await api.get(`/manufacturers-find-duplicates?search=${encodeURIComponent(mfrConsolSearch)}`);
      setMfrConsolSearchResults(res.data.manufacturers || []);
      setMfrConsolMode('search');
      // Auto-select the one with most amps as keeper
      const results = res.data.manufacturers || [];
      if (results.length > 0) {
        setMfrConsolKeepers({ search: results[0].manufacturer_id });
      }
    } catch (err) {
      console.error('Failed to search manufacturers:', err);
      setMfrConsolMessage({ type: 'error', text: 'Search failed' });
    } finally {
      setMfrConsolLoading(false);
    }
  };

  const setMfrKeeper = (groupKey, manufacturerId) => {
    setMfrConsolKeepers(prev => ({ ...prev, [groupKey]: manufacturerId }));
  };

  const toggleMfrGroup = (groupKey) => {
    setMfrConsolExpanded(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const toggleMfrExclude = (groupKey, manufacturerId) => {
    setMfrConsolExcluded(prev => {
      const current = new Set(prev[groupKey] || []);
      if (current.has(manufacturerId)) {
        current.delete(manufacturerId);
      } else {
        current.add(manufacturerId);
        // If excluded member was the keeper, clear keeper
        if (mfrConsolKeepers[groupKey] === manufacturerId) {
          setMfrConsolKeepers(p => ({ ...p, [groupKey]: undefined }));
        }
      }
      return { ...prev, [groupKey]: current };
    });
  };

  const isMfrExcluded = (groupKey, manufacturerId) => {
    return (mfrConsolExcluded[groupKey] || new Set()).has(manufacturerId);
  };

  const toggleMfrExampleAmps = async (manufacturerId) => {
    // If already showing, collapse
    if (mfrExampleAmps[manufacturerId]?.amps) {
      setMfrExampleAmps(prev => { const next = { ...prev }; delete next[manufacturerId]; return next; });
      return;
    }
    // Fetch examples
    setMfrExampleAmps(prev => ({ ...prev, [manufacturerId]: { loading: true, amps: null } }));
    try {
      const res = await api.get(`/amplifiers?manufacturer_id=${manufacturerId}&pageSize=5`);
      const amps = res.data.data || res.data || [];
      // Fetch first image artifact for each amp in parallel
      const ampsWithImages = await Promise.all(amps.map(async (amp) => {
        try {
          const artRes = await api.get(`/amplifier_artifacts?amplifier_id=${amp.amplifier_id}`);
          const artifacts = artRes.data || [];
          const firstImage = artifacts.find(a => a.mime_type && a.mime_type.startsWith('image/'));
          return { ...amp, imageArtifactId: firstImage?.artifact_id || null };
        } catch {
          return { ...amp, imageArtifactId: null };
        }
      }));
      setMfrExampleAmps(prev => ({ ...prev, [manufacturerId]: { loading: false, amps: ampsWithImages } }));
    } catch (err) {
      console.error('Failed to fetch example amps:', err);
      setMfrExampleAmps(prev => ({ ...prev, [manufacturerId]: { loading: false, amps: [], error: true } }));
    }
  };

  const startMfrRename = (manufacturer) => {
    setMfrRenaming({ manufacturer_id: manufacturer.manufacturer_id, name: manufacturer.manufacturer_name });
  };

  const saveMfrRename = async () => {
    if (!mfrRenaming || !mfrRenaming.name.trim()) return;
    setMfrRenameSaving(true);
    try {
      await api.put(`/manufacturers/${mfrRenaming.manufacturer_id}`, {
        manufacturer_name: mfrRenaming.name.trim(),
      });
      const newName = mfrRenaming.name.trim();
      const mfrId = mfrRenaming.manufacturer_id;
      setMfrConsolMessage({ type: 'success', text: `Renamed manufacturer #${mfrId} to "${newName}"` });
      setMfrRenaming(null);
      // Update name in-place (preserves scroll position) instead of full reload
      if (mfrConsolMode === 'auto') {
        setMfrConsolGroups(prev => prev.map(g => ({
          ...g,
          members: g.members.map(m =>
            m.manufacturer_id === mfrId ? { ...m, manufacturer_name: newName } : m
          ),
        })));
      } else {
        setMfrConsolSearchResults(prev => prev.map(m =>
          m.manufacturer_id === mfrId ? { ...m, manufacturer_name: newName } : m
        ));
      }
      // Also update the amp-tab manufacturer list in the background
      loadManufacturers();
    } catch (err) {
      console.error('Rename failed:', err);
      setMfrConsolMessage({ type: 'error', text: `Rename failed: ${err.response?.data?.error || err.message}` });
    } finally {
      setMfrRenameSaving(false);
    }
  };

  const executeConsolidation = async (groupKey, members) => {
    const keeperId = mfrConsolKeepers[groupKey];
    if (!keeperId) {
      setMfrConsolMessage({ type: 'error', text: 'Please select a keeper manufacturer first' });
      return;
    }

    const excluded = mfrConsolExcluded[groupKey] || new Set();
    const sourceIds = members
      .filter(m => m.manufacturer_id !== keeperId && !excluded.has(m.manufacturer_id))
      .map(m => m.manufacturer_id);

    if (sourceIds.length === 0) {
      setMfrConsolMessage({ type: 'error', text: 'No source manufacturers to consolidate (all are excluded or keeper)' });
      return;
    }

    const keeperName = members.find(m => m.manufacturer_id === keeperId)?.manufacturer_name;
    const sourceMembersActive = members.filter(m => m.manufacturer_id !== keeperId && !excluded.has(m.manufacturer_id));
    const excludedNames = members.filter(m => excluded.has(m.manufacturer_id)).map(m => m.manufacturer_name);
    if (!window.confirm(
      `Consolidate ${sourceMembersActive.length} manufacturer(s) into "${keeperName}"?\n\n` +
      `Will move:\n${sourceMembersActive.map(m => `  • "${m.manufacturer_name}" → "${keeperName}"`).join('\n')}\n\n` +
      (excludedNames.length > 0 ? `Excluded (will NOT be touched):\n${excludedNames.map(n => `  ⊘ "${n}"`).join('\n')}\n\n` : '') +
      `Empty manufacturers will be deleted. This cannot be undone.`
    )) return;

    setMfrConsolExecuting(groupKey);
    setMfrConsolMessage(null);
    try {
      const res = await api.post('/manufacturers-consolidate', {
        keeper_id: keeperId,
        source_ids: sourceIds,
      });
      setMfrConsolResults(res.data);
      setMfrConsolMessage({
        type: 'success',
        text: `Consolidated! Moved ${res.data.total_amps_moved} amps, removed ${res.data.manufacturers_removed} manufacturer(s). "${keeperName}" now has ${parseInt(res.data.keeper?.amp_count || 0).toLocaleString()} amps.`,
      });
      // Update list in-place (preserves scroll position) instead of full reload
      if (mfrConsolMode === 'auto') {
        setMfrConsolGroups(prev => {
          return prev.map(g => {
            if (g.group_key !== groupKey) return g;
            // Keep only the keeper and any excluded members
            const remainingMembers = g.members.filter(
              m => m.manufacturer_id === keeperId || excluded.has(m.manufacturer_id)
            );
            // Update keeper's amp count from response
            const updatedMembers = remainingMembers.map(m =>
              m.manufacturer_id === keeperId
                ? { ...m, amp_count: parseInt(res.data.keeper?.amp_count || m.amp_count) }
                : m
            );
            return { ...g, members: updatedMembers, total_members: updatedMembers.length };
          }).filter(g => g.members.length > 1); // Remove groups with only 1 member left (no duplicates)
        });
      } else {
        // Search mode: remove consolidated sources from results
        setMfrConsolSearchResults(prev => {
          const remaining = prev.filter(
            m => m.manufacturer_id === keeperId || excluded.has(m.manufacturer_id)
          );
          // Update keeper's amp count
          return remaining.map(m =>
            m.manufacturer_id === keeperId
              ? { ...m, amp_count: parseInt(res.data.keeper?.amp_count || m.amp_count) }
              : m
          );
        });
      }
      // Refresh amp duplicate stats (lightweight, doesn't affect scroll)
      loadStats();
    } catch (err) {
      console.error('Consolidation failed:', err);
      setMfrConsolMessage({ type: 'error', text: `Consolidation failed: ${err.response?.data?.error || err.message}` });
    } finally {
      setMfrConsolExecuting(null);
    }
  };

  // ─── Render Helpers ─────────────────────────────────────────────────────
  const filteredMfrs = manufacturers.filter(m =>
    m.manufacturer_name.toLowerCase().includes(mfrSearch.toLowerCase())
  );

  const approvedCount = Object.values(aiActions).filter(v => v.status === 'approved').length;
  const pendingCount = Object.values(aiActions).filter(v => v.status === 'pending').length;

  const renderDataBadge = (label, count, colorKey) => {
    if (!count || parseInt(count) === 0) return null;
    const c = badgeColors[colorKey] || badgeColors.children;
    return (
      <span key={label} style={{ ...styles.dataBadge, background: c.bg, color: c.color }}>
        {count} {label}
      </span>
    );
  };

  const renderActionCard = useCallback((model) => {
    const entry = aiActions[model];
    if (!entry) return null;
    const { action, status, rejectHistory } = entry;
    const typeColor = actionTypeColors[action.type] || actionTypeColors.skip;
    const isReAnalyzingThis = reAnalyzing === model;
    const isRejectingThis = rejectingModel === model;

    return (
      <div style={{
        ...styles.actionCard,
        borderColor: status === 'approved' ? '#68d391' : status === 'rejected' ? '#fc8181' : '#e2e8f0',
        background: status === 'approved' ? '#f0fff4' : status === 'rejected' ? '#fff5f5' : '#fffff0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <span style={{ ...styles.actionType, background: typeColor.bg, color: typeColor.color }}>
              {action.type}
            </span>
            {status === 'approved' && <span style={styles.statusApproved}>✓ Approved</span>}
            {status === 'rejected' && <span style={styles.statusRejected}>✗ Rejected</span>}
            {status === 'pending' && <span style={styles.statusPending}>Pending</span>}
            {rejectHistory && rejectHistory.length > 0 && (
              <span style={{ fontSize: '0.72rem', color: '#9b2c2c', marginLeft: '8px' }}>
                (attempt {rejectHistory.length + 1})
              </span>
            )}
          </div>
        </div>

        <div style={styles.actionReason}>{action.reason}</div>

        {action.type === 'merge' && (
          <div style={styles.actionDetail}>
            Keep ID {action.keeper_id} → make children: [{action.child_ids?.join(', ')}]
          </div>
        )}
        {action.type === 'rename' && (
          <div style={styles.actionDetail}>
            {action.renames?.map((r, i) => (
              <div key={i}>ID {r.id} → "{r.new_model}"</div>
            ))}
          </div>
        )}
        {action.type === 'delete' && (
          <div style={styles.actionDetail}>
            Delete IDs: [{action.delete_ids?.join(', ')}]
          </div>
        )}

        {/* Rejection history */}
        {rejectHistory && rejectHistory.length > 0 && (
          <div style={styles.rejectHistory}>
            <div style={styles.rejectHistoryLabel}>Previous Attempts</div>
            {rejectHistory.map((h, i) => (
              <div key={i} style={styles.rejectHistoryItem}>
                <strong>Attempt {i + 1}:</strong> AI suggested <em>{h.suggestion.type}</em> — 
                You said: "{h.reason}"
              </div>
            ))}
          </div>
        )}

        {/* Rejection comment box — shown when user clicks Reject */}
        {isRejectingThis && (
          <div style={styles.rejectCommentBox}>
            <label style={styles.rejectCommentLabel}>
              Why are you rejecting this suggestion?
            </label>
            <textarea
              style={styles.rejectTextarea}
              value={rejectComment}
              onChange={e => setRejectComment(e.target.value)}
              placeholder="e.g., These are actually different variants (Head vs Combo), not duplicates..."
              autoFocus
            />
            <div style={styles.rejectCommentActions}>
              <button
                style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnOutline }}
                onClick={cancelRejection}
              >
                Cancel
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnDanger }}
                onClick={() => submitRejection(model)}
              >
                Submit Rejection
              </button>
            </div>
          </div>
        )}

        {/* Re-analyzing spinner */}
        {isReAnalyzingThis && (
          <div style={{ marginTop: '10px', padding: '8px 12px', background: '#faf5ff', borderRadius: '6px', fontSize: '0.85rem', color: '#553c9a' }}>
            Asking Gemini for a revised suggestion...
          </div>
        )}

        {status !== 'skip' && action.type !== 'skip' && (
          <div style={styles.actionButtons}>
            {status !== 'approved' && !isRejectingThis && (
              <button
                style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSuccess }}
                onClick={() => setActionStatus(model, 'approved')}
              >
                ✓ Approve
              </button>
            )}
            {status !== 'rejected' && !isRejectingThis && (
              <button
                style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnDanger }}
                onClick={() => { setRejectingModel(model); setRejectComment(''); }}
              >
                ✗ Reject
              </button>
            )}
            {status === 'rejected' && !isRejectingThis && (
              <button
                style={{ ...styles.reAnalyzeBtn, ...(isReAnalyzingThis ? styles.btnDisabled : {}) }}
                onClick={() => reAnalyzeGroup(model)}
                disabled={isReAnalyzingThis}
              >
                Re-analyze with AI
              </button>
            )}
            {status !== 'pending' && !isRejectingThis && (
              <button
                style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnOutline }}
                onClick={() => { setActionStatus(model, 'pending'); setRejectingModel(null); }}
              >
                ↺ Reset
              </button>
            )}
          </div>
        )}
      </div>
    );
  }, [aiActions, rejectingModel, rejectComment, reAnalyzing, reAnalyzeGroup, submitRejection]);

  // ─── Access check ───────────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.errorMessage}>
          Admin access required for duplicate cleanup.
        </div>
      </div>
    );
  }

  // ─── Main Render ────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Data Cleanup Tools</h1>
        <p style={styles.subtitle}>
          AI-assisted tools for finding and resolving duplicate amplifiers and consolidating manufacturers.
        </p>
      </div>

      {/* Tab Bar */}
      <div style={styles.tabBar}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'amps' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('amps')}
        >
          Amp Duplicates
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'manufacturers' ? styles.tabActive : {}) }}
          onClick={() => { setActiveTab('manufacturers'); if (mfrConsolGroups.length === 0 && !mfrConsolLoading) loadMfrDuplicates(); }}
        >
          Manufacturer Consolidation
        </button>
      </div>

      {/* ═══════════════════ AMP DUPLICATES TAB ═══════════════════ */}
      {activeTab === 'amps' && (
      <>

      {/* Stats Dashboard */}
      {stats && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{parseInt(stats.total_amps || 0).toLocaleString()}</div>
            <div style={styles.statLabel}>Total Amps</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{parseInt(stats.total_parents || 0).toLocaleString()}</div>
            <div style={styles.statLabel}>Parents</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{parseInt(stats.total_children || 0).toLocaleString()}</div>
            <div style={styles.statLabel}>Children</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: '#e53e3e' }}>
              {parseInt(stats.mfrs_with_dups || 0).toLocaleString()}
            </div>
            <div style={styles.statLabel}>Mfrs w/ Dupes</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: '#dd6b20' }}>
              {parseInt(stats.dup_groups || 0).toLocaleString()}
            </div>
            <div style={styles.statLabel}>Dup Groups</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: '#c53030' }}>
              {parseInt(stats.extra_to_resolve || 0).toLocaleString()}
            </div>
            <div style={styles.statLabel}>Extras to Resolve</div>
          </div>
        </div>
      )}

      {/* Messages */}
      {message && (
        <div style={message.type === 'success' ? styles.successMessage : styles.errorMessage}>
          {message.text}
        </div>
      )}

      {/* Main Layout */}
      <div style={styles.mainLayout}>
        {/* Left: Manufacturer List */}
        <div style={styles.mfrPanel}>
          <div style={styles.panelHeader}>
            Manufacturers ({filteredMfrs.length})
            {manufacturers.length > 0 && !autoProcessing && (
              <button
                style={{ ...styles.btn, ...styles.btnSmall, background: '#9f7aea', color: 'white', marginLeft: 'auto', fontSize: '0.72rem' }}
                onClick={autoProcessAll}
              >
                Auto-Process All
              </button>
            )}
            {autoProcessing && (
              <button
                style={{ ...styles.btn, ...styles.btnSmall, background: '#e53e3e', color: 'white', marginLeft: 'auto', fontSize: '0.72rem' }}
                onClick={() => { autoProcessCancelledRef.current = true; }}
              >
                ⏹ Cancel
              </button>
            )}
          </div>

          {/* Auto-process progress */}
          {autoProcessing && autoProcessProgress && (
            <div style={{ padding: '10px 12px', background: '#f0e6ff', borderBottom: '1px solid #d6bcfa', fontSize: '0.82rem' }}>
              <div style={{ fontWeight: '600', color: '#553c9a', marginBottom: '4px' }}>
                Auto-Processing: {autoProcessProgress.current} / {autoProcessProgress.total}
              </div>
              <div style={{ color: '#6b46c1', marginBottom: '6px' }}>
                {autoProcessProgress.mfrName} — {autoProcessProgress.action}
              </div>
              <div style={{ ...styles.progressBar, overflow: 'hidden', height: '6px' }}>
                <div style={{
                  ...styles.progressFill,
                  background: '#9f7aea',
                  width: `${Math.round((autoProcessProgress.current / autoProcessProgress.total) * 100)}%`,
                  transition: 'width 0.3s ease',
                }} />
              </div>
              {autoProcessProgress.results && autoProcessProgress.results.length > 0 && (
                <div style={{ marginTop: '6px', maxHeight: '120px', overflowY: 'auto', fontSize: '0.75rem' }}>
                  {autoProcessProgress.results.slice(-5).map((r, i) => (
                    <div key={i} style={{ color: r.status === 'success' ? '#276749' : r.status === 'error' ? '#c53030' : '#718096' }}>
                      {r.status === 'success' ? '✓' : r.status === 'error' ? '✗' : r.status === 'partial' ? '⚠️' : '→'} {r.mfr}: {r.detail}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={styles.mfrSearch}>
            <input
              type="text"
              placeholder="Search manufacturers..."
              value={mfrSearch}
              onChange={e => setMfrSearch(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          <div style={styles.mfrList}>
            {loading ? (
              <div style={styles.loadingSpinner}>Loading...</div>
            ) : filteredMfrs.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#a0aec0' }}>
                {mfrSearch ? 'No matches found' : 'No duplicates detected! ✓'}
              </div>
            ) : (
              filteredMfrs.map(mfr => (
                <div
                  key={mfr.manufacturer_id}
                  style={{
                    ...styles.mfrItem,
                    ...(selectedMfr?.manufacturer_id === mfr.manufacturer_id ? styles.mfrItemActive : {}),
                  }}
                  onClick={() => selectManufacturer(mfr)}
                  onMouseEnter={e => { if (selectedMfr?.manufacturer_id !== mfr.manufacturer_id) e.currentTarget.style.background = '#f7fafc'; }}
                  onMouseLeave={e => { if (selectedMfr?.manufacturer_id !== mfr.manufacturer_id) e.currentTarget.style.background = ''; }}
                >
                  <div>
                    <div style={styles.mfrName}>{mfr.manufacturer_name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#a0aec0' }}>
                      {mfr.dup_groups} group{mfr.dup_groups > 1 ? 's' : ''} • {mfr.total_amps} total amps
                    </div>
                  </div>
                  <span style={styles.mfrBadge}>{mfr.total_dup_amps} dupes</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: Duplicate Groups / Actions */}
        <div style={styles.rightPanel}>
          <div style={styles.rightPanelHeader}>
            <span style={styles.rightPanelHeaderTitle}>
              {selectedMfr ? `${selectedMfr.manufacturer_name} — Duplicate Groups` : 'Select a Manufacturer'}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {selectedMfr && duplicateGroups.length > 0 && (
                <button
                  style={{
                    ...styles.btn,
                    ...styles.btnPrimary,
                    ...(analyzing ? styles.btnDisabled : {}),
                  }}
                  onClick={analyzeWithAI}
                  disabled={analyzing}
                >
                  {analyzing ? 'Analyzing...' : 'Analyze with AI'}
                </button>
              )}
              {approvedCount > 0 && (
                <button
                  style={{
                    ...styles.btn,
                    ...styles.btnSuccess,
                    ...(executing ? styles.btnDisabled : {}),
                  }}
                  onClick={() => executeApproved()}
                  disabled={executing}
                >
                  {executing ? 'Executing...' : `Execute ${approvedCount} Approved`}
                </button>
              )}
            </div>
          </div>

          <div style={styles.rightPanelContent}>
            {/* Action bar when AI actions exist */}
            {Object.keys(aiActions).length > 0 && (
              <div style={{ ...styles.infoBox, marginBottom: '16px' }}>
                <div style={styles.infoText}>
                  <strong>AI Suggestions Ready</strong> — {approvedCount} approved, {pendingCount} pending review, {Object.values(aiActions).filter(v => v.status === 'rejected').length} rejected
                  <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                    <button
                      style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSuccess }}
                      onClick={approveAll}
                    >
                      ✓ Approve All (non-skip)
                    </button>
                    {pendingCount > 0 && (
                      <button
                        style={{ ...styles.btn, ...styles.btnSmall, background: '#9f7aea', color: 'white', ...(executing ? styles.btnDisabled : {}) }}
                        onClick={approveAllAndExecute}
                        disabled={executing}
                      >
                        {executing ? 'Running...' : 'Approve All & Execute'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Progress bar */}
            {analyzing && (
              <AnalysisTimer groupCount={duplicateGroups.length} startTime={analysisStartTime} />
            )}

            {/* Empty state */}
            {!selectedMfr && (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>←</div>
                <div style={{ fontSize: '1.1rem', fontWeight: '500', marginBottom: '8px' }}>
                  Select a manufacturer to start
                </div>
                <div style={{ fontSize: '0.9rem' }}>
                  Choose a manufacturer from the left panel to view and resolve duplicate amplifiers.
                </div>
              </div>
            )}

            {/* Loading */}
            {loadingGroups && (
              <div style={styles.loadingSpinner}>
                <div>Loading duplicate groups...</div>
              </div>
            )}

            {/* Duplicate Groups */}
            {!loadingGroups && selectedMfr && duplicateGroups.length === 0 && (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>✓</div>
                <div style={{ fontSize: '1.1rem', fontWeight: '500' }}>
                  No duplicates remaining for {selectedMfr.manufacturer_name}!
                </div>
              </div>
            )}

            {!loadingGroups && duplicateGroups.map(group => (
              <div key={group.model} style={styles.groupCard}>
                <div
                  style={styles.groupHeader}
                  onClick={() => toggleGroup(group.model)}
                >
                  <div>
                    <span style={{ marginRight: '8px', color: '#718096' }}>
                      {expandedGroups[group.model] ? '▼' : '▶'}
                    </span>
                    <span style={styles.groupModel}>{group.model}</span>
                  </div>
                  <span style={styles.groupCount}>
                    {group.amps.length} duplicates
                  </span>
                </div>

                {expandedGroups[group.model] && (
                  <div style={styles.groupBody}>
                    {group.amps.map(amp => {
                      const ampUrl = selectedMfr?.manufacturer_slug && amp.slug
                        ? `/amps/${selectedMfr.manufacturer_slug}/${amp.slug}`
                        : `/amplifier-components/${amp.amplifier_id}`;
                      return (
                      <div key={amp.amplifier_id} style={styles.ampRow}>
                        <div style={styles.ampId}>
                          <a
                            href={ampUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#2563eb', textDecoration: 'none' }}
                            title="Open amp details in new window"
                          >
                            #{amp.amplifier_id}
                          </a>
                        </div>
                        <div style={styles.ampDetails}>
                          <div style={styles.ampModel}>
                            <a
                              href={ampUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#2d3748', textDecoration: 'none', borderBottom: '1px dashed #a0aec0' }}
                              title="Open amp details in new window"
                              onMouseEnter={e => { e.target.style.color = '#2563eb'; e.target.style.borderBottomColor = '#2563eb'; }}
                              onMouseLeave={e => { e.target.style.color = '#2d3748'; e.target.style.borderBottomColor = '#a0aec0'; }}
                            >
                              {amp.model}
                            </a>
                            {amp.name && amp.name.trim() !== '' && (
                              <span style={{ fontWeight: '400', color: '#718096', marginLeft: '8px' }}>
                                ({amp.name})
                              </span>
                            )}
                            <span style={{ marginLeft: '6px', fontSize: '0.75rem', color: '#a0aec0', cursor: 'pointer' }}
                              onClick={() => window.open(ampUrl, '_blank')}
                              title="Open in new window"
                            >Link</span>
                          </div>
                          <div style={styles.ampMeta}>
                            {(amp.start_year || amp.end_year) && (
                              <span style={styles.ampMetaItem}>
                                {amp.start_year || '?'} – {amp.end_year || '?'}
                              </span>
                            )}
                            {amp.description && amp.description.trim() !== '' && (
                              <span style={styles.ampMetaItem}>
                                Has description
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={styles.ampDataBadges}>
                          {renderDataBadge('children', amp.child_count, 'children')}
                          {renderDataBadge('artifacts', amp.artifact_count, 'artifacts')}
                          {renderDataBadge('components', amp.component_count, 'components')}
                          {renderDataBadge('discussions', amp.discussion_count, 'discussions')}
                        </div>
                      </div>
                      );
                    })}

                    {/* AI Action for this group */}
                    {renderActionCard(group.model)}
                  </div>
                )}
              </div>
            ))}

            {/* Execution Results */}
            {executionResults && (
              <div style={styles.summaryBox}>
                <div style={styles.summaryTitle}>Execution Results</div>
                {executionResults.map((r, i) => (
                  <div key={i} style={styles.summaryItem}>
                    {r.status === 'success' ? '✓' : r.status === 'error' ? '✗' : '→'} <strong>{r.type?.toUpperCase()}</strong> — {r.model}
                    {r.status === 'success' && <span style={{ marginLeft: '8px', color: '#276749' }}>(done)</span>}
                    {r.error && <span style={{ marginLeft: '8px', color: '#c53030' }}>Error: {r.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      </>
      )}

      {/* ═══════════════════ MANUFACTURER CONSOLIDATION TAB ═══════════════════ */}
      {activeTab === 'manufacturers' && (
      <>
        {/* Messages */}
        {mfrConsolMessage && (
          <div style={mfrConsolMessage.type === 'success' ? styles.successMessage : styles.errorMessage}>
            {mfrConsolMessage.text}
          </div>
        )}

        {/* Search bar */}
        <div style={styles.mfrConsolSearch}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.82rem', fontWeight: '600', color: '#4a5568', display: 'block', marginBottom: '4px' }}>
              Search for similar manufacturer names
            </label>
            <input
              type="text"
              placeholder='e.g. "Fender" or "Mesa" — leave blank for auto-detect'
              value={mfrConsolSearch}
              onChange={e => setMfrConsolSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') searchMfrDuplicates(); }}
              style={styles.mfrConsolSearchInput}
            />
          </div>
          <button
            style={{ ...styles.btn, ...styles.btnPrimary, padding: '10px 20px', ...(mfrConsolLoading ? styles.btnDisabled : {}) }}
            onClick={searchMfrDuplicates}
            disabled={mfrConsolLoading}
          >
            {mfrConsolLoading ? 'Searching...' : 'Search'}
          </button>
          {mfrConsolMode === 'search' && (
            <button
              style={{ ...styles.btn, ...styles.btnOutline, padding: '10px 20px' }}
              onClick={() => { setMfrConsolSearch(''); loadMfrDuplicates(); }}
            >
              ↺ Show All
            </button>
          )}
        </div>

        {/* Info box */}
        <div style={styles.infoBox}>
          <div style={styles.infoText}>
            <strong>How it works:</strong> Select which manufacturer to keep (the "keeper") in each group.
            All amps from the other manufacturers will be moved to the keeper, then the empty manufacturers are deleted.
            The keeper is auto-selected as the one with the most amps.
            Use <strong>⊘ Exclude</strong> to skip individual entries you don't want to merge.
            Use <strong>Rename</strong> to change a manufacturer's name before or after consolidation.
          </div>
        </div>

        {/* Loading */}
        {mfrConsolLoading && (
          <div style={styles.loadingSpinner}>
            <div>Scanning for duplicate manufacturers...</div>
          </div>
        )}

        {/* Search Results Mode */}
        {!mfrConsolLoading && mfrConsolMode === 'search' && mfrConsolSearchResults.length > 0 && (
          <div style={styles.mfrGroupCard}>
            <div style={styles.mfrGroupHeader}>
              <div>
                <span style={styles.mfrGroupTitle}>
                  Search results for "{mfrConsolSearch}" — {mfrConsolSearchResults.length} manufacturers
                </span>
              </div>
              <span style={styles.ampCountBadge}>
                {mfrConsolSearchResults.reduce((s, m) => s + parseInt(m.amp_count), 0).toLocaleString()} total amps
              </span>
            </div>
            <div>
              {mfrConsolSearchResults.map(m => {
                const isExcluded = isMfrExcluded('search', m.manufacturer_id);
                const isKeeper = mfrConsolKeepers.search === m.manufacturer_id;
                const isSource = mfrConsolKeepers.search && !isKeeper && !isExcluded;
                const isBeingRenamed = mfrRenaming?.manufacturer_id === m.manufacturer_id;
                return (
                  <React.Fragment key={m.manufacturer_id}>
                  <div
                    style={{
                      ...styles.mfrMemberRow,
                      background: isExcluded ? '#f7fafc' : isKeeper ? '#f0fff4' : 'white',
                      ...(isExcluded ? { opacity: 0.5 } : {}),
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      {isBeingRenamed ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <input
                            type="text"
                            value={mfrRenaming.name}
                            onChange={e => setMfrRenaming(prev => ({ ...prev, name: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') saveMfrRename(); if (e.key === 'Escape') setMfrRenaming(null); }}
                            style={styles.renameInput}
                            autoFocus
                          />
                          <button
                            style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSuccess }}
                            onClick={() => saveMfrRename()}
                            disabled={mfrRenameSaving}
                          >
                            {mfrRenameSaving ? '…' : '✓ Save'}
                          </button>
                          <button
                            style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnOutline }}
                            onClick={() => setMfrRenaming(null)}
                          >
                            ✕
                          </button>
                        </span>
                      ) : (
                        <>
                          <span
                            style={{ ...styles.mfrMemberName, ...(isExcluded ? { textDecoration: 'line-through' } : {}), cursor: 'pointer', textDecoration: mfrExampleAmps[m.manufacturer_id] ? 'underline' : undefined }}
                            onClick={() => toggleMfrExampleAmps(m.manufacturer_id)}
                            title="Click to see example amplifiers"
                          >
                            {m.manufacturer_name}
                            <span style={{ fontSize: '0.72rem', color: '#a0aec0', marginLeft: '4px' }}>{mfrExampleAmps[m.manufacturer_id]?.amps ? '▼' : ''}</span>
                          </span>
                          <span style={{ fontSize: '0.78rem', color: '#a0aec0', marginLeft: '8px' }}>#{m.manufacturer_id}</span>
                          {isKeeper && <span style={{ ...styles.keeperBadge, marginLeft: '8px' }}>keeper</span>}
                          {isSource && <span style={{ ...styles.sourceBadge, marginLeft: '8px' }}>→ merge</span>}
                          {isExcluded && <span style={{ ...styles.excludedBadge, marginLeft: '8px' }}>⊘ excluded</span>}
                        </>
                      )}
                    </div>
                    <div style={styles.mfrMemberMeta}>
                      <span style={styles.ampCountBadge}>{parseInt(m.amp_count).toLocaleString()} amps</span>
                      {!isExcluded && !isKeeper && (
                        <button
                          style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnOutline, fontSize: '0.72rem' }}
                          onClick={() => startMfrRename(m)}
                          title="Rename this manufacturer"
                        >
                          Rename
                        </button>
                      )}
                      {!isExcluded ? (
                        <button
                          style={{
                            ...styles.btn,
                            ...styles.btnSmall,
                            ...(isKeeper ? { background: '#c6f6d5', color: '#22543d' } : styles.btnOutline),
                          }}
                          onClick={() => setMfrKeeper('search', m.manufacturer_id)}
                        >
                          {isKeeper ? 'Keeper' : 'Set as Keeper'}
                        </button>
                      ) : null}
                      <button
                        style={{
                          ...styles.btn,
                          ...styles.btnSmall,
                          ...(isExcluded
                            ? { background: '#bee3f8', color: '#2b6cb0', fontSize: '0.72rem' }
                            : { background: '#fed7e2', color: '#97266d', fontSize: '0.72rem' }),
                        }}
                        onClick={() => toggleMfrExclude('search', m.manufacturer_id)}
                        title={isExcluded ? 'Include this manufacturer in consolidation' : 'Exclude from consolidation'}
                      >
                        {isExcluded ? '↩ Include' : '⊘ Exclude'}
                      </button>
                    </div>
                  </div>
                  {mfrExampleAmps[m.manufacturer_id] && (
                    <div style={styles.exampleAmpsPanel}>
                      {mfrExampleAmps[m.manufacturer_id].loading ? (
                        <div style={{ color: '#a0aec0' }}>Loading example amps...</div>
                      ) : mfrExampleAmps[m.manufacturer_id].error ? (
                        <div style={{ color: '#e53e3e' }}>Failed to load examples</div>
                      ) : mfrExampleAmps[m.manufacturer_id].amps?.length === 0 ? (
                        <div style={{ color: '#a0aec0' }}>No amplifiers found for this manufacturer</div>
                      ) : (
                        <>
                          <div style={{ fontWeight: '600', marginBottom: '4px', color: '#718096' }}>Example amps ({Math.min(5, parseInt(m.amp_count))} of {parseInt(m.amp_count).toLocaleString()}):</div>
                          {mfrExampleAmps[m.manufacturer_id].amps.map(amp => (
                            <div key={amp.amplifier_id} style={styles.exampleAmpRow}>
                              {amp.imageArtifactId ? (
                                <img
                                  src={`${API_URL}/amplifier_artifacts/${amp.imageArtifactId}`}
                                  alt={amp.model || amp.name}
                                  style={{ ...styles.exampleAmpThumb, cursor: 'pointer' }}
                                  onMouseEnter={(e) => handleThumbEnter(e, amp.imageArtifactId)}
                                  onMouseLeave={handleThumbLeave}
                                  onClick={() => setHoverPreview(prev => prev ? null : { src: `${API_URL}/amplifier_artifacts/${amp.imageArtifactId}`, x: 0, y: 0, pinned: true })}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div style={styles.exampleAmpNoImage}>—</div>
                              )}
                              <div>
                                <div style={styles.exampleAmpModel}>{amp.model || amp.name}</div>
                                {amp.name && amp.name !== amp.model && <div style={{ color: '#718096', fontSize: '0.78rem' }}>{amp.name}</div>}
                                {(amp.start_year || amp.end_year) && (
                                  <span style={styles.exampleAmpYear}>
                                    ({amp.start_year || '?'}–{amp.end_year || 'present'})
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </React.Fragment>
                );
              })}
            </div>
            {mfrConsolSearchResults.length > 1 && (
              <div style={styles.consolActions}>
                {(() => {
                  const excluded = mfrConsolExcluded.search || new Set();
                  const searchKeeper = mfrConsolKeepers.search;
                  const activeCount = mfrConsolSearchResults.filter(m => m.manufacturer_id !== searchKeeper && !excluded.has(m.manufacturer_id)).length;
                  return (
                    <button
                      style={{
                        ...styles.btn,
                        ...styles.btnSuccess,
                        ...(mfrConsolExecuting === 'search' || activeCount === 0 ? styles.btnDisabled : {}),
                      }}
                      onClick={() => executeConsolidation('search', mfrConsolSearchResults.map(m => ({
                        manufacturer_id: m.manufacturer_id,
                        manufacturer_name: m.manufacturer_name,
                        amp_count: parseInt(m.amp_count),
                      })))}
                      disabled={mfrConsolExecuting === 'search' || activeCount === 0}
                    >
                      {mfrConsolExecuting === 'search'
                        ? 'Consolidating...'
                        : activeCount === 0
                          ? '(All excluded or keeper)'
                          : `Consolidate ${activeCount} into Keeper`}
                    </button>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {!mfrConsolLoading && mfrConsolMode === 'search' && mfrConsolSearchResults.length === 0 && mfrConsolSearch && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>—</div>
            <div style={{ fontSize: '1.1rem', fontWeight: '500' }}>No manufacturers matching "{mfrConsolSearch}"</div>
          </div>
        )}

        {/* Auto-Detect Mode */}
        {!mfrConsolLoading && mfrConsolMode === 'auto' && mfrConsolGroups.length === 0 && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>✓</div>
            <div style={{ fontSize: '1.1rem', fontWeight: '500' }}>No duplicate manufacturers detected!</div>
            <div style={{ fontSize: '0.9rem', marginTop: '8px' }}>Try searching for a specific name to check manually.</div>
          </div>
        )}

        {!mfrConsolLoading && mfrConsolMode === 'auto' && mfrConsolGroups.map(group => {
          const isExpanded = mfrConsolExpanded[group.group_key];
          const keeperId = mfrConsolKeepers[group.group_key];
          return (
            <div key={group.group_key} style={styles.mfrGroupCard}>
              <div
                style={styles.mfrGroupHeader}
                onClick={() => toggleMfrGroup(group.group_key)}
              >
                <div>
                  <span style={{ marginRight: '8px', color: '#718096' }}>
                    {isExpanded ? '▼' : '▶'}
                  </span>
                  <span style={styles.mfrGroupTitle}>
                    "{group.group_key}" — {group.total_members} manufacturers
                  </span>
                </div>
                <span style={styles.ampCountBadge}>{group.total_amps.toLocaleString()} total amps</span>
              </div>

              {isExpanded && (
                <>
                  <div>
                    {group.members.map(m => {
                      const isExcluded = isMfrExcluded(group.group_key, m.manufacturer_id);
                      const isKeeper = keeperId === m.manufacturer_id;
                      const isSource = keeperId && !isKeeper && !isExcluded;
                      const isBeingRenamed = mfrRenaming?.manufacturer_id === m.manufacturer_id;
                      return (
                        <React.Fragment key={m.manufacturer_id}>
                        <div
                          style={{
                            ...styles.mfrMemberRow,
                            background: isExcluded ? '#f7fafc' : isKeeper ? '#f0fff4' : 'white',
                            ...(isExcluded ? { opacity: 0.5 } : {}),
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            {isBeingRenamed ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <input
                                  type="text"
                                  value={mfrRenaming.name}
                                  onChange={e => setMfrRenaming(prev => ({ ...prev, name: e.target.value }))}
                                  onKeyDown={e => { if (e.key === 'Enter') saveMfrRename(); if (e.key === 'Escape') setMfrRenaming(null); }}
                                  style={styles.renameInput}
                                  autoFocus
                                  onClick={e => e.stopPropagation()}
                                />
                                <button
                                  style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSuccess }}
                                  onClick={(e) => { e.stopPropagation(); saveMfrRename(); }}
                                  disabled={mfrRenameSaving}
                                >
                                  {mfrRenameSaving ? '…' : '✓ Save'}
                                </button>
                                <button
                                  style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnOutline }}
                                  onClick={(e) => { e.stopPropagation(); setMfrRenaming(null); }}
                                >
                                  ✕
                                </button>
                              </span>
                            ) : (
                              <>
                                <span
                                  style={{ ...styles.mfrMemberName, ...(isExcluded ? { textDecoration: 'line-through' } : {}), cursor: 'pointer', textDecoration: mfrExampleAmps[m.manufacturer_id] ? 'underline' : undefined }}
                                  onClick={(e) => { e.stopPropagation(); toggleMfrExampleAmps(m.manufacturer_id); }}
                                  title="Click to see example amplifiers"
                                >
                                  {m.manufacturer_name}
                                  <span style={{ fontSize: '0.72rem', color: '#a0aec0', marginLeft: '4px' }}>{mfrExampleAmps[m.manufacturer_id]?.amps ? '▼' : ''}</span>
                                </span>
                                <span style={{ fontSize: '0.78rem', color: '#a0aec0', marginLeft: '8px' }}>#{m.manufacturer_id}</span>
                                {isKeeper && <span style={{ ...styles.keeperBadge, marginLeft: '8px' }}>keeper</span>}
                                {isSource && <span style={{ ...styles.sourceBadge, marginLeft: '8px' }}>→ merge</span>}
                                {isExcluded && <span style={{ ...styles.excludedBadge, marginLeft: '8px' }}>⊘ excluded</span>}
                              </>
                            )}
                          </div>
                          <div style={styles.mfrMemberMeta}>
                            <span style={styles.ampCountBadge}>{m.amp_count.toLocaleString()} amps</span>
                            {!isExcluded && !isKeeper && (
                              <button
                                style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnOutline, fontSize: '0.72rem' }}
                                onClick={(e) => { e.stopPropagation(); startMfrRename(m); }}
                                title="Rename this manufacturer"
                              >
                                Rename
                              </button>
                            )}
                            {!isExcluded ? (
                              <button
                                style={{
                                  ...styles.btn,
                                  ...styles.btnSmall,
                                  ...(isKeeper ? { background: '#c6f6d5', color: '#22543d' } : styles.btnOutline),
                                }}
                                onClick={(e) => { e.stopPropagation(); setMfrKeeper(group.group_key, m.manufacturer_id); }}
                              >
                                {isKeeper ? 'Keeper' : 'Set as Keeper'}
                              </button>
                            ) : null}
                            <button
                              style={{
                                ...styles.btn,
                                ...styles.btnSmall,
                                ...(isExcluded
                                  ? { background: '#bee3f8', color: '#2b6cb0', fontSize: '0.72rem' }
                                  : { background: '#fed7e2', color: '#97266d', fontSize: '0.72rem' }),
                              }}
                              onClick={(e) => { e.stopPropagation(); toggleMfrExclude(group.group_key, m.manufacturer_id); }}
                              title={isExcluded ? 'Include this manufacturer in consolidation' : 'Exclude from consolidation'}
                            >
                              {isExcluded ? '↩ Include' : '⊘ Exclude'}
                            </button>
                          </div>
                        </div>
                        {mfrExampleAmps[m.manufacturer_id] && (
                          <div style={styles.exampleAmpsPanel}>
                            {mfrExampleAmps[m.manufacturer_id].loading ? (
                              <div style={{ color: '#a0aec0' }}>Loading example amps...</div>
                            ) : mfrExampleAmps[m.manufacturer_id].error ? (
                              <div style={{ color: '#e53e3e' }}>Failed to load examples</div>
                            ) : mfrExampleAmps[m.manufacturer_id].amps?.length === 0 ? (
                              <div style={{ color: '#a0aec0' }}>No amplifiers found for this manufacturer</div>
                            ) : (
                              <>
                                <div style={{ fontWeight: '600', marginBottom: '4px', color: '#718096' }}>Example amps ({Math.min(5, m.amp_count)} of {m.amp_count.toLocaleString()}):</div>
                                {mfrExampleAmps[m.manufacturer_id].amps.map(amp => (
                                  <div key={amp.amplifier_id} style={styles.exampleAmpRow}>
                                    {amp.imageArtifactId ? (
                                      <img
                                        src={`${API_URL}/amplifier_artifacts/${amp.imageArtifactId}`}
                                        alt={amp.model || amp.name}
                                        style={{ ...styles.exampleAmpThumb, cursor: 'pointer' }}
                                        onMouseEnter={(e) => handleThumbEnter(e, amp.imageArtifactId)}
                                        onMouseLeave={handleThumbLeave}
                                        onClick={() => setHoverPreview(prev => prev ? null : { src: `${API_URL}/amplifier_artifacts/${amp.imageArtifactId}`, x: 0, y: 0, pinned: true })}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    ) : (
                                      <div style={styles.exampleAmpNoImage}>—</div>
                                    )}
                                    <div>
                                      <div style={styles.exampleAmpModel}>{amp.model || amp.name}</div>
                                      {amp.name && amp.name !== amp.model && <div style={{ color: '#718096', fontSize: '0.78rem' }}>{amp.name}</div>}
                                      {(amp.start_year || amp.end_year) && (
                                        <span style={styles.exampleAmpYear}>
                                          ({amp.start_year || '?'}–{amp.end_year || 'present'})
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        )}
                      </React.Fragment>
                      );
                    })}
                  </div>
                  <div style={styles.consolActions}>
                    {(() => {
                      const excluded = mfrConsolExcluded[group.group_key] || new Set();
                      const activeCount = group.members.filter(m => m.manufacturer_id !== keeperId && !excluded.has(m.manufacturer_id)).length;
                      return (
                        <button
                          style={{
                            ...styles.btn,
                            ...styles.btnSuccess,
                            ...(mfrConsolExecuting === group.group_key || activeCount === 0 ? styles.btnDisabled : {}),
                          }}
                          onClick={() => executeConsolidation(group.group_key, group.members)}
                          disabled={!!mfrConsolExecuting || activeCount === 0}
                        >
                          {mfrConsolExecuting === group.group_key
                            ? 'Consolidating...'
                            : activeCount === 0
                              ? '(All excluded or keeper)'
                              : `Consolidate ${activeCount} into Keeper`}
                        </button>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </>
      )}

      {/* Image hover preview overlay */}
      {hoverPreview && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9998,
            ...(hoverPreview.pinned ? { background: 'rgba(0,0,0,0.6)', cursor: 'pointer' } : { pointerEvents: 'none' }),
          }}
          onClick={() => setHoverPreview(null)}
        >
          <div
            style={{
              position: 'fixed',
              ...(hoverPreview.pinned
                ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
                : { top: Math.min(hoverPreview.y, window.innerHeight - 420), left: Math.min(hoverPreview.x, window.innerWidth - 420) }),
              zIndex: 9999,
              background: '#fff',
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              padding: '6px',
              pointerEvents: 'auto',
            }}
          >
            <img
              src={hoverPreview.src}
              alt="Preview"
              style={{
                maxWidth: hoverPreview.pinned ? '80vw' : '400px',
                maxHeight: hoverPreview.pinned ? '80vh' : '400px',
                borderRadius: '6px',
                display: 'block',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DuplicateCleanupScreen;
